import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button, Spin, Carousel, Typography, Modal, Row, Col, Form, Input, DatePicker, Select, message } from 'antd';
import {
  UserOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  InstagramOutlined,
  ClockCircleOutlined,
  PictureOutlined,
  CloseOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { API_BASE_URL } from '../../config/api';
import { categoriaLabel } from '../../utils/categoriaLabel';

const { Title, Text, Paragraph } = Typography;
const PORTADA_CACHE_KEY = 'public_portada_cache_v1';

const buildAuth0Url = () => {
  const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const auth0ClientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
  const auth0RedirectUri = process.env.REACT_APP_AUTH0_REDIRECT_URI || window.location.origin;
  if (!auth0Domain || !auth0ClientId) return null;

  return `https://${auth0Domain}/authorize?` +
    `client_id=${auth0ClientId}&` +
    `redirect_uri=${encodeURIComponent(auth0RedirectUri)}&` +
    `response_type=code&` +
    `scope=openid profile email&` +
    `audience=${encodeURIComponent(process.env.REACT_APP_AUTH0_AUDIENCE || '')}`;
};

const Portada = () => {
  const navigate = useNavigate();
  const [formAcademia] = Form.useForm();
  const [data, setData] = useState({
    empresa: null,
    academia: null,
    difusiones: [],
    config: { club: true, academia: true, sacarTurnoRequiereLogin: false },
  });
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [modalDifusion, setModalDifusion] = useState(null);
  const [modalAcademiaOpen, setModalAcademiaOpen] = useState(false);
  const [mostrarFormularioAcademia, setMostrarFormularioAcademia] = useState(false);
  const [categoriasAcademia, setCategoriasAcademia] = useState([]);
  const [loadingCategoriasAcademia, setLoadingCategoriasAcademia] = useState(false);
  const [submittingSolicitudAcademia, setSubmittingSolicitudAcademia] = useState(false);
  const [cantidadVisibleDifusiones, setCantidadVisibleDifusiones] = useState(4);
  const modalCarouselRef = useRef(null);
  const cardCarouselRefs = useRef({});

  const MAX_DIFUSIONES_INICIAL = 4;
  const difusionesVisibles = (data.difusiones || []).slice(0, cantidadVisibleDifusiones);
  const hayMasDifusiones = (data.difusiones || []).length > MAX_DIFUSIONES_INICIAL && cantidadVisibleDifusiones < (data.difusiones || []).length;

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/public/portada`);
        if (res.ok) {
          const json = await res.json();
          try {
            localStorage.setItem(PORTADA_CACHE_KEY, JSON.stringify({
              empresa: json.empresa || null,
              academia: json.academia || null,
              config: json.config || { club: true, academia: true, sacarTurnoRequiereLogin: false },
              cachedAt: Date.now(),
            }));
          } catch (e) {
            // cache no bloqueante
          }
          setData({
            empresa: json.empresa || null,
            academia: json.academia || null,
            difusiones: json.difusiones || [],
            config: json.config || { club: true, academia: true, sacarTurnoRequiereLogin: false },
          });
        }
      } catch (err) {
        console.error('Error al cargar portada:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleSacarTurno = () => {
    if (data?.config?.sacarTurnoRequiereLogin) {
      const auth0Url = buildAuth0Url();
      if (!auth0Url) {
        message.error('Auth0 no está configurado correctamente.');
        return;
      }
      window.location.href = auth0Url;
      return;
    }
    navigate('/sacar-turno');
  };

  const handleIngresoAlumnoAcademia = () => {
    const auth0Url = buildAuth0Url();
    if (!auth0Url) {
      message.error('Auth0 no está configurado correctamente.');
      return;
    }
    window.location.href = auth0Url;
  };

  const abrirFormularioAcademia = async () => {
    setMostrarFormularioAcademia(true);
    if (categoriasAcademia.length > 0) return;

    setLoadingCategoriasAcademia(true);
    try {
      const res = await fetch(`${API_BASE_URL}/categorias`);
      if (!res.ok) throw new Error('No se pudieron cargar categorías');
      const json = await res.json();
      setCategoriasAcademia(Array.isArray(json) ? json.filter((c) => c.cat_activo) : []);
    } catch (err) {
      message.error('No se pudieron cargar las categorías de academia.');
    } finally {
      setLoadingCategoriasAcademia(false);
    }
  };

  const cerrarModalAcademia = () => {
    setModalAcademiaOpen(false);
    setMostrarFormularioAcademia(false);
    setSubmittingSolicitudAcademia(false);
    formAcademia.resetFields();
  };

  const enviarSolicitudAcademia = async (values) => {
    setSubmittingSolicitudAcademia(true);
    try {
      const body = {
        nombre: values.nombre,
        apellido: values.apellido,
        fechaNacimiento: values.fechaNacimiento?.format('YYYY-MM-DD'),
        sexo: values.sexo,
        email: values.email?.trim().toLowerCase(),
        telefono: values.telefono,
        categoria: values.categoria,
      };

      const res = await fetch(`${API_BASE_URL}/solicitudes-alumnos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json();

      if (!res.ok) {
        message.error(json?.message || 'Error al enviar la solicitud');
        return;
      }

      message.success('Solicitud enviada correctamente. Pronto te contactaremos.');
      cerrarModalAcademia();
    } catch (err) {
      message.error('Error al enviar la solicitud');
    } finally {
      setSubmittingSolicitudAcademia(false);
    }
  };

  const empresa = data.empresa;
  const academia = data.academia;
  const clubHabilitado = data?.config?.club !== false;
  const academiaHabilitada = data?.config?.academia !== false;
  const headerEntidad = !clubHabilitado && academiaHabilitada && academia ? academia : empresa;
  const mostrarClub = clubHabilitado && Boolean(empresa);
  const mostrarAcademia =
    academiaHabilitada && (
    Boolean(academia?.nombre) ||
    Boolean(academia?.direccion) ||
    Boolean(academia?.telefono) ||
    Boolean(academia?.instagram) ||
    Boolean(academia?.foto_base64) ||
    Boolean(academia?.descripcion)
    );
  const difusiones = React.useMemo(() => {
    const list = data.difusiones || [];
    return [...list].sort((a, b) => new Date(b.dif_fecha_creacion || 0) - new Date(a.dif_fecha_creacion || 0));
  }, [data.difusiones]);

  const formatearFechaPublicacion = (fecha) => {
    if (!fecha) return null;
    return new Date(fecha).toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: '#0d0d0d',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin size="large" tip="Cargando..." />
      </div>
    );
  }

  const mapUrl =
    empresa?.latitud != null && empresa?.longitud != null
      ? `https://www.openstreetmap.org/export/embed.html?bbox=${empresa.longitud - 0.003},${empresa.latitud - 0.002},${empresa.longitud + 0.003},${empresa.latitud + 0.002}&layer=mapnik&marker=${empresa.latitud},${empresa.longitud}`
      : null;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#0d0d0d',
        color: '#e8e8e8',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      {/* Header */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: 'rgba(13, 13, 13, 0.95)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          padding: isMobile ? '12px 16px' : '16px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: isMobile ? 'nowrap' : 'wrap',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
          {headerEntidad?.foto_base64 ? (
            <img
              src={`data:${headerEntidad.foto_tipo || 'image/jpeg'};base64,${headerEntidad.foto_base64}`}
              alt=""
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                objectFit: 'cover',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            />
          ) : null}
          <Title
            level={4}
            style={{
              margin: 0,
              color: '#fff',
              fontWeight: 600,
              fontSize: isMobile ? 16 : undefined,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {headerEntidad?.nombre || (clubHabilitado ? 'Club' : 'Academia')}
          </Title>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto', flexShrink: 0 }}>
          <Link to="/login">
            <Button
              type="default"
              size={isMobile ? 'middle' : 'large'}
              icon={<UserOutlined />}
              style={{
                color: '#e8e8e8',
                borderColor: 'rgba(255,255,255,0.2)',
                borderRadius: 10,
              }}
            >
              Login
            </Button>
          </Link>
        </div>
      </header>

      <main style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? 20 : 40 }}>
        {/* Hero: en desktop una sola card con logo+nombre+botón y datos del club; en mobile solo contenido centrado */}
        {mostrarClub && (
        <section
          style={{
            marginTop: isMobile ? 12 : 24,
            marginBottom: isMobile ? 32 : 56,
          }}
        >
          {!isMobile ? (
            <div
              style={{
                background: 'rgba(255,255,255,0.04)',
                borderRadius: 20,
                padding: 40,
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              }}
            >
              <Row gutter={40} align="middle">
                <Col xs={24} lg={12}>
                  <div style={{ textAlign: 'center' }}>
                    {empresa?.foto_base64 && (
                      <div
                        style={{
                          width: 160,
                          height: 160,
                          margin: '0 auto 24px',
                          borderRadius: 24,
                          overflow: 'hidden',
                          border: '3px solid rgba(255,255,255,0.08)',
                          boxShadow: '0 20px 50px rgba(0,0,0,0.4)',
                        }}
                      >
                        <img
                          src={`data:${empresa.foto_tipo};base64,${empresa.foto_base64}`}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                    )}
                    <Title level={1} style={{ color: '#fff', marginBottom: 12, fontWeight: 700 }}>
                      {empresa?.nombre || 'Bienvenidos'}
                    </Title>
                    <Button
                      type="primary"
                      size="large"
                      icon={<CalendarOutlined />}
                      onClick={handleSacarTurno}
                      style={{
                        marginTop: 24,
                        background: '#22c55e',
                        borderColor: '#22c55e',
                        fontWeight: 600,
                        paddingLeft: 28,
                        paddingRight: 28,
                        height: 48,
                        borderRadius: 12,
                        fontSize: 16,
                      }}
                    >
                      Sacar turno
                    </Button>
                  </div>
                </Col>
                <Col xs={24} lg={12}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {empresa?.direccion && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <EnvironmentOutlined style={{ fontSize: 18, color: '#22c55e', marginTop: 2 }} />
                        <div>
                          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Dirección</Text>
                          <Text style={{ color: '#e8e8e8' }}>{empresa.direccion}</Text>
                          {empresa.localidad_nombre && (
                            <Text style={{ color: 'rgba(255,255,255,0.6)', display: 'block', marginTop: 2 }}>
                              {empresa.localidad_nombre}
                            </Text>
                          )}
                        </div>
                      </div>
                    )}
                    {empresa?.telefono && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <PhoneOutlined style={{ fontSize: 18, color: '#22c55e', marginTop: 2 }} />
                        <div>
                          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Teléfono</Text>
                          <a href={`tel:${empresa.telefono}`} style={{ color: '#e8e8e8' }}>{empresa.telefono}</a>
                        </div>
                      </div>
                    )}
                    {(empresa?.horario_desde || empresa?.horario_hasta) && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <ClockCircleOutlined style={{ fontSize: 18, color: '#22c55e', marginTop: 2 }} />
                        <div>
                          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Horario</Text>
                          <Text style={{ color: '#e8e8e8' }}>
                            {empresa.horario_desde && empresa.horario_hasta
                              ? `${empresa.horario_desde} - ${empresa.horario_hasta}`
                              : empresa.horario_desde || empresa.horario_hasta}
                          </Text>
                        </div>
                      </div>
                    )}
                    {empresa?.instagram && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 0 }}>
                        <InstagramOutlined style={{ fontSize: 18, color: '#22c55e', marginTop: 2, flexShrink: 0 }} />
                        <div style={{ minWidth: 0, overflow: 'hidden' }}>
                          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Instagram</Text>
                          <a
                            href={
                              empresa.instagram.startsWith('http')
                                ? empresa.instagram
                                : `https://instagram.com/${empresa.instagram.replace('@', '')}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#e8e8e8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                          >
                            {empresa.instagram}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          ) : (
            <div style={{ textAlign: 'center' }}>
              {empresa?.foto_base64 && (
                <div
                  style={{
                    width: 120,
                    height: 120,
                    margin: '0 auto 24px',
                    borderRadius: 24,
                    overflow: 'hidden',
                    border: '3px solid rgba(255,255,255,0.08)',
                    boxShadow: '0 20px 50px rgba(0,0,0,0.4)',
                  }}
                >
                  <img
                    src={`data:${empresa.foto_tipo};base64,${empresa.foto_base64}`}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              )}
              <Title level={1} style={{ color: '#fff', marginBottom: 12, fontWeight: 700 }}>
                {empresa?.nombre || 'Bienvenidos'}
              </Title>
              <Button
                type="primary"
                size="large"
                icon={<CalendarOutlined />}
                onClick={handleSacarTurno}
                style={{
                  marginTop: 24,
                  background: '#22c55e',
                  borderColor: '#22c55e',
                  fontWeight: 600,
                  paddingLeft: 28,
                  paddingRight: 28,
                  height: 48,
                  borderRadius: 12,
                  fontSize: 16,
                }}
              >
                Sacar turno
              </Button>

              <section
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  borderRadius: 20,
                  padding: 24,
                  marginTop: 20,
                  border: '1px solid rgba(255,255,255,0.06)',
                  textAlign: 'left',
                }}
              >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
                  {empresa?.direccion && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <EnvironmentOutlined style={{ fontSize: 20, color: '#22c55e', marginTop: 2 }} />
                      <div>
                        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Dirección</Text>
                        <Text style={{ color: '#e8e8e8' }}>{empresa.direccion}</Text>
                        {empresa.localidad_nombre && (
                          <Text style={{ color: 'rgba(255,255,255,0.6)', display: 'block', marginTop: 4 }}>{empresa.localidad_nombre}</Text>
                        )}
                      </div>
                    </div>
                  )}
                  {empresa?.telefono && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <PhoneOutlined style={{ fontSize: 20, color: '#22c55e', marginTop: 2 }} />
                      <div>
                        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Teléfono</Text>
                        <a href={`tel:${empresa.telefono}`} style={{ color: '#e8e8e8' }}>{empresa.telefono}</a>
                      </div>
                    </div>
                  )}
                  {(empresa?.horario_desde || empresa?.horario_hasta) && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <ClockCircleOutlined style={{ fontSize: 20, color: '#22c55e', marginTop: 2 }} />
                      <div>
                        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Horario</Text>
                        <Text style={{ color: '#e8e8e8' }}>
                          {empresa.horario_desde && empresa.horario_hasta ? `${empresa.horario_desde} - ${empresa.horario_hasta}` : empresa.horario_desde || empresa.horario_hasta}
                        </Text>
                      </div>
                    </div>
                  )}
                  {empresa?.instagram && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 0 }}>
                      <InstagramOutlined style={{ fontSize: 20, color: '#22c55e', marginTop: 2, flexShrink: 0 }} />
                      <div style={{ minWidth: 0, overflow: 'hidden' }}>
                        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Instagram</Text>
                        <a
                          href={empresa.instagram.startsWith('http') ? empresa.instagram : `https://instagram.com/${empresa.instagram.replace('@', '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#e8e8e8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                        >
                          {empresa.instagram}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </div>
          )}
        </section>
        )}

        {mostrarAcademia && (
          <section
            style={{
              marginBottom: isMobile ? 32 : 48,
              background: 'rgba(255,255,255,0.04)',
              borderRadius: 20,
              padding: isMobile ? 24 : 40,
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            }}
          >
            <Row gutter={isMobile ? [24, 24] : [40, 40]} align="middle">
              <Col xs={24} lg={12}>
                <div style={{ textAlign: 'center' }}>
                  {academia?.foto_base64 && (
                    <div
                      style={{
                        width: isMobile ? 120 : 160,
                        height: isMobile ? 120 : 160,
                        margin: '0 auto 24px',
                        borderRadius: 24,
                        overflow: 'hidden',
                        border: '3px solid rgba(255,255,255,0.08)',
                        boxShadow: '0 20px 50px rgba(0,0,0,0.4)',
                      }}
                    >
                      <img
                        src={`data:${academia.foto_tipo || 'image/jpeg'};base64,${academia.foto_base64}`}
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                  )}
                  <Title level={1} style={{ color: '#fff', marginBottom: 12, fontWeight: 700 }}>
                    {academia?.nombre || 'Bienvenidos'}
                  </Title>
                  {academia?.descripcion && (
                    <Paragraph
                      style={{
                        fontSize: isMobile ? 16 : 18,
                        color: 'rgba(255,255,255,0.75)',
                        maxWidth: 480,
                        margin: '0 auto',
                        lineHeight: 1.6,
                      }}
                    >
                      {academia.descripcion.length > 200
                        ? `${academia.descripcion.substring(0, 200)}…`
                        : academia.descripcion}
                    </Paragraph>
                  )}
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => setModalAcademiaOpen(true)}
                    style={{
                      marginTop: 24,
                      background: '#22c55e',
                      borderColor: '#22c55e',
                      fontWeight: 600,
                      paddingLeft: 28,
                      paddingRight: 28,
                      height: 48,
                      borderRadius: 12,
                      fontSize: 16,
                    }}
                  >
                    Academia
                  </Button>
                </div>
              </Col>
              <Col xs={24} lg={12}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {academia?.direccion && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <EnvironmentOutlined style={{ fontSize: 18, color: '#22c55e', marginTop: 2 }} />
                      <div>
                        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Dirección</Text>
                        <Text style={{ color: '#e8e8e8' }}>{academia.direccion}</Text>
                        {academia.localidad_nombre && (
                          <Text style={{ color: 'rgba(255,255,255,0.6)', display: 'block', marginTop: 2 }}>
                            {academia.localidad_nombre}
                          </Text>
                        )}
                      </div>
                    </div>
                  )}
                  {academia?.telefono && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <PhoneOutlined style={{ fontSize: 18, color: '#22c55e', marginTop: 2 }} />
                      <div>
                        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Teléfono</Text>
                        <a href={`tel:${academia.telefono}`} style={{ color: '#e8e8e8' }}>{academia.telefono}</a>
                      </div>
                    </div>
                  )}
                  {(academia?.horario_desde || academia?.horario_hasta) && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <ClockCircleOutlined style={{ fontSize: 18, color: '#22c55e', marginTop: 2 }} />
                      <div>
                        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Horario</Text>
                        <Text style={{ color: '#e8e8e8' }}>
                          {academia.horario_desde && academia.horario_hasta
                            ? `${academia.horario_desde} - ${academia.horario_hasta}`
                            : academia.horario_desde || academia.horario_hasta}
                        </Text>
                      </div>
                    </div>
                  )}
                  {academia?.instagram && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 0 }}>
                      <InstagramOutlined style={{ fontSize: 18, color: '#22c55e', marginTop: 2, flexShrink: 0 }} />
                      <div style={{ minWidth: 0, overflow: 'hidden' }}>
                        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Instagram</Text>
                        <a
                          href={academia.instagram.startsWith('http') ? academia.instagram : `https://instagram.com/${academia.instagram.replace('@', '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#e8e8e8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                        >
                          {academia.instagram}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </section>
        )}

        {/* Desktop: Novedades y Mapa lado a lado (50% cada uno). Mobile: apilados */}
        {(difusiones.length > 0 || mapUrl) && (
          <section style={{ marginBottom: isMobile ? 32 : 48 }}>
            {!isMobile ? (
              <Row gutter={24} style={{ alignItems: 'stretch' }}>
                <Col xs={24} lg={12}>
                  <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {difusiones.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, flex: 1 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                        {difusionesVisibles.map((dif) => {
                          const imagenesConSrc = (dif.imagenes || []).filter((i) => i.dif_imagen_base64);
                          const primeraImagen = imagenesConSrc[0];
                          return (
                            <div
                              key={dif.dif_id}
                              role="button"
                              tabIndex={0}
                              onClick={() => setModalDifusion(dif)}
                              onKeyDown={(e) => e.key === 'Enter' && setModalDifusion(dif)}
                              style={{
                                width: 'calc(50% - 8px)',
                                minWidth: 140,
                                background: 'rgba(255,255,255,0.03)',
                                borderRadius: 16,
                                overflow: 'hidden',
                                border: '1px solid rgba(255,255,255,0.08)',
                                cursor: 'pointer',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-4px)';
                                e.currentTarget.style.boxShadow = '0 12px 28px rgba(0,0,0,0.35)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'none';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              <div
                                style={{
                                  width: '100%',
                                  height: 0,
                                  paddingBottom: '100%',
                                  background: '#1a1a1a',
                                  position: 'relative',
                                  overflow: 'hidden',
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {primeraImagen ? (
                                  imagenesConSrc.length > 1 ? (
                                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, height: '100%', minHeight: 180 }}>
                                      <Carousel
                                        ref={(r) => { cardCarouselRefs.current[dif.dif_id] = r; }}
                                        dots
                                        dotPosition="bottom"
                                        style={{ height: '100%', width: '100%', minHeight: 180 }}
                                      >
                                        {imagenesConSrc.map((img, idx) => (
                                          <div key={idx} style={{ height: 180, minHeight: 180 }}>
                                            <img
                                              src={`data:${img.dif_imagen_tipo};base64,${img.dif_imagen_base64}`}
                                              alt=""
                                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                          </div>
                                        ))}
                                      </Carousel>
                                      <Button
                                        type="text"
                                        icon={<LeftOutlined />}
                                        onClick={(e) => { e.stopPropagation(); cardCarouselRefs.current[dif.dif_id]?.prev(); }}
                                        style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 3, color: '#fff', background: 'rgba(0,0,0,0.4)', width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none' }}
                                      />
                                      <Button
                                        type="text"
                                        icon={<RightOutlined />}
                                        onClick={(e) => { e.stopPropagation(); cardCarouselRefs.current[dif.dif_id]?.next(); }}
                                        style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 3, color: '#fff', background: 'rgba(0,0,0,0.4)', width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none' }}
                                      />
                                    </div>
                                  ) : (
                                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
                                      <img
                                        src={`data:${primeraImagen.dif_imagen_tipo};base64,${primeraImagen.dif_imagen_base64}`}
                                        alt=""
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                      />
                                    </div>
                                  )
                                ) : (
                                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <PictureOutlined style={{ fontSize: 48, color: '#333' }} />
                                  </div>
                                )}
                                {imagenesConSrc.length > 1 && (
                                  <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 2, background: 'rgba(0,0,0,0.5)', borderRadius: 8, padding: '4px 8px', fontSize: 12, color: '#fff' }}>
                                    {imagenesConSrc.length} fotos
                                  </div>
                                )}
                              </div>
                              {formatearFechaPublicacion(dif.dif_fecha_creacion) && (
                                <div style={{ padding: '8px 12px 0', fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
                                  {formatearFechaPublicacion(dif.dif_fecha_creacion)}
                                </div>
                              )}
                              <div style={{ padding: 12 }}>
                                <Paragraph ellipsis={{ rows: 5, suffix: '... Ver más' }} style={{ margin: 0, color: 'rgba(255,255,255,0.9)', fontSize: 13, lineHeight: 1.5 }}>
                                  {dif.dif_descripcion || '—'}
                                </Paragraph>
                              </div>
                            </div>
                          );
                        })}
                        </div>
                        {hayMasDifusiones && (
                          <Button
                            type="default"
                            onClick={() => setCantidadVisibleDifusiones(difusiones.length)}
                            style={{ alignSelf: 'flex-start', color: '#22c55e', borderColor: '#22c55e' }}
                          >
                            Ver más
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div style={{ flex: 1, background: 'rgba(255,255,255,0.02)', borderRadius: 16, border: '1px dashed rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
                        <Text type="secondary">Sin novedades por ahora</Text>
                      </div>
                    )}
                  </div>
                </Col>
                <Col xs={24} lg={12}>
                  <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {mapUrl ? (
                      <>
                        <div style={{ position: 'relative', paddingBottom: '75%', height: 0, background: '#1a1a1a', borderRadius: 12, overflow: 'hidden', flex: 1, minHeight: 280 }}>
                          <iframe
                            title="Ubicación del club"
                            src={mapUrl}
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                          />
                        </div>
                        <div style={{ padding: 12, background: 'rgba(0,0,0,0.2)', borderRadius: '0 0 12px 12px' }}>
                          <a
                            href={`https://www.google.com/maps?q=${empresa.latitud},${empresa.longitud}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#22c55e', fontSize: 14 }}
                          >
                            Abrir en Google Maps →
                          </a>
                        </div>
                      </>
                    ) : (
                      <div style={{ flex: 1, background: 'rgba(255,255,255,0.02)', borderRadius: 16, border: '1px dashed rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
                        <Text type="secondary">Sin ubicación cargada</Text>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            ) : (
              <>
                {difusiones.length > 0 && (
                  <div style={{ marginBottom: 32 }}>
                    <Row gutter={[16, 16]}>
                      {difusionesVisibles.map((dif) => {
                        const imagenesConSrc = (dif.imagenes || []).filter((i) => i.dif_imagen_base64);
                        const primeraImagen = imagenesConSrc[0];
                        return (
                          <Col xs={24} sm={12} md={8} key={dif.dif_id}>
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => setModalDifusion(dif)}
                              onKeyDown={(e) => e.key === 'Enter' && setModalDifusion(dif)}
                              style={{
                                background: 'rgba(255,255,255,0.03)',
                                borderRadius: 16,
                                overflow: 'hidden',
                                border: '1px solid rgba(255,255,255,0.08)',
                                cursor: 'pointer',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-4px)';
                                e.currentTarget.style.boxShadow = '0 12px 28px rgba(0,0,0,0.35)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'none';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              <div
                                style={{
                                  width: '100%',
                                  height: 0,
                                  paddingBottom: '100%',
                                  background: '#1a1a1a',
                                  position: 'relative',
                                  overflow: 'hidden',
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {primeraImagen ? (
                                  imagenesConSrc.length > 1 ? (
                                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, height: '100%', minHeight: 220 }}>
                                      <Carousel
                                        ref={(r) => { cardCarouselRefs.current[dif.dif_id] = r; }}
                                        dots
                                        dotPosition="bottom"
                                        style={{ height: '100%', width: '100%', minHeight: 220 }}
                                      >
                                        {imagenesConSrc.map((img, idx) => (
                                          <div key={idx} style={{ height: 220, minHeight: 220 }}>
                                            <img
                                              src={`data:${img.dif_imagen_tipo};base64,${img.dif_imagen_base64}`}
                                              alt=""
                                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                          </div>
                                        ))}
                                      </Carousel>
                                      <Button
                                        type="text"
                                        icon={<LeftOutlined />}
                                        onClick={(e) => { e.stopPropagation(); cardCarouselRefs.current[dif.dif_id]?.prev(); }}
                                        style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 3, color: '#fff', background: 'rgba(0,0,0,0.4)', width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none' }}
                                      />
                                      <Button
                                        type="text"
                                        icon={<RightOutlined />}
                                        onClick={(e) => { e.stopPropagation(); cardCarouselRefs.current[dif.dif_id]?.next(); }}
                                        style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 3, color: '#fff', background: 'rgba(0,0,0,0.4)', width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none' }}
                                      />
                                    </div>
                                  ) : (
                                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
                                      <img
                                        src={`data:${primeraImagen.dif_imagen_tipo};base64,${primeraImagen.dif_imagen_base64}`}
                                        alt=""
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                      />
                                    </div>
                                  )
                                ) : (
                                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <PictureOutlined style={{ fontSize: 48, color: '#333' }} />
                                  </div>
                                )}
                                {imagenesConSrc.length > 1 && (
                                  <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 2, background: 'rgba(0,0,0,0.5)', borderRadius: 8, padding: '4px 8px', fontSize: 12, color: '#fff' }}>
                                    {imagenesConSrc.length} fotos
                                  </div>
                                )}
                              </div>
                              {formatearFechaPublicacion(dif.dif_fecha_creacion) && (
                                <div style={{ padding: '8px 16px 0', fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
                                  {formatearFechaPublicacion(dif.dif_fecha_creacion)}
                                </div>
                              )}
                              <div style={{ padding: 16 }}>
                                <Paragraph ellipsis={{ rows: 5, suffix: '... Ver más' }} style={{ margin: 0, color: 'rgba(255,255,255,0.9)', fontSize: 14, lineHeight: 1.5 }}>
                                  {dif.dif_descripcion || '—'}
                                </Paragraph>
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                    {hayMasDifusiones && (
                      <Button
                        type="default"
                        onClick={() => setCantidadVisibleDifusiones(difusiones.length)}
                        style={{ marginTop: 16, color: '#22c55e', borderColor: '#22c55e' }}
                      >
                        Ver más
                      </Button>
                    )}
                  </div>
                )}
                {mapUrl && (
                  <div style={{ borderRadius: 20, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
                    <div style={{ position: 'relative', paddingBottom: '40%', height: 0, background: '#1a1a1a' }}>
                      <iframe title="Ubicación del club" src={mapUrl} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }} />
                    </div>
                    <div style={{ padding: 12, background: 'rgba(0,0,0,0.2)' }}>
                      <a href={`https://www.google.com/maps?q=${empresa.latitud},${empresa.longitud}`} target="_blank" rel="noopener noreferrer" style={{ color: '#22c55e', fontSize: 14 }}>
                        Abrir en Google Maps →
                      </a>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* CTA final */}
        <section
          style={{
            textAlign: 'center',
            padding: isMobile ? 40 : 60,
            background: 'linear-gradient(180deg, rgba(34,197,94,0.08) 0%, transparent 100%)',
            borderRadius: 20,
            border: '1px solid rgba(34,197,94,0.15)',
          }}
        >
          <Title level={3} style={{ color: '#fff', marginBottom: 12 }}>
            ¿Listo para jugar?
          </Title>
          <Paragraph style={{ color: 'rgba(255,255,255,0.7)', marginBottom: 24 }}>
            Reservá tu turno en pocos pasos.
          </Paragraph>
          <Button
            type="primary"
            size="large"
            icon={<CalendarOutlined />}
            onClick={handleSacarTurno}
            style={{
              background: '#22c55e',
              borderColor: '#22c55e',
              fontWeight: 600,
              paddingLeft: 32,
              paddingRight: 32,
              height: 50,
              borderRadius: 12,
              fontSize: 16,
            }}
          >
            Sacar turno
          </Button>
        </section>
      </main>

      <footer
        style={{
          marginTop: 48,
          padding: 24,
          textAlign: 'center',
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Text type="secondary" style={{ fontSize: 13 }}>
          {empresa?.nombre || 'Club'} · Desarrollado por Geno Software
        </Text>
      </footer>

      {/* Modal tipo Instagram: difusión en grande con carrusel de imágenes */}
      <Modal
        open={modalAcademiaOpen}
        onCancel={cerrarModalAcademia}
        footer={null}
        width={isMobile ? '100%' : 680}
        title={mostrarFormularioAcademia ? 'Empezar Academia' : 'Academia'}
        styles={{
          content: { borderRadius: 16 },
        }}
      >
        {!mostrarFormularioAcademia ? (
          <div>
            <Paragraph style={{ color: 'rgba(255,255,255,0.72)', marginBottom: 16 }}>
              Elegí cómo querés continuar en la academia.
            </Paragraph>
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={12}>
                <Button
                  type="primary"
                  onClick={handleIngresoAlumnoAcademia}
                  style={{
                    width: '100%',
                    height: isMobile ? 72 : 120,
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 4,
                    fontWeight: 700,
                    background: '#d8ff3e',
                    borderColor: '#d8ff3e',
                    color: '#0e1400',
                  }}
                >
                  <UserOutlined style={{ fontSize: 18 }} />
                  <span>Ingreso alumno</span>
                  {!isMobile && <span style={{ fontSize: 12, fontWeight: 500, opacity: 0.75 }}>Ya tengo cuenta</span>}
                </Button>
              </Col>
              <Col xs={24} sm={12}>
                <Button
                  onClick={abrirFormularioAcademia}
                  style={{
                    width: '100%',
                    height: isMobile ? 72 : 120,
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 4,
                    fontWeight: 700,
                    borderColor: 'rgba(255,255,255,0.25)',
                    color: '#f5f5f5',
                    background: 'rgba(255,255,255,0.02)',
                  }}
                >
                  <CalendarOutlined style={{ fontSize: 18 }} />
                  <span>Empezar Academia</span>
                  {!isMobile && <span style={{ fontSize: 12, fontWeight: 500, opacity: 0.75 }}>Completar solicitud</span>}
                </Button>
              </Col>
            </Row>
          </div>
        ) : (
          <Form form={formAcademia} layout="vertical" onFinish={enviarSolicitudAcademia}>
            <Row gutter={12}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="nombre"
                  label="Nombre"
                  rules={[
                    { required: true, message: 'El nombre es requerido' },
                    { max: 100, message: 'Máximo 100 caracteres' },
                  ]}
                >
                  <Input placeholder="Nombre" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="apellido"
                  label="Apellido"
                  rules={[
                    { required: true, message: 'El apellido es requerido' },
                    { max: 100, message: 'Máximo 100 caracteres' },
                  ]}
                >
                  <Input placeholder="Apellido" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="fechaNacimiento" label="Fecha de nacimiento" rules={[{ required: true, message: 'La fecha es requerida' }]}>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>

            <Form.Item name="sexo" label="Sexo" rules={[{ required: true, message: 'El sexo es requerido' }]}>
              <Select placeholder="Seleccionar sexo">
                <Select.Option value="Masculino">Masculino</Select.Option>
                <Select.Option value="Femenino">Femenino</Select.Option>
                <Select.Option value="Otro">Otro</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'El email es requerido' },
                { type: 'email', message: 'Email inválido' },
              ]}
            >
              <Input placeholder="ejemplo@email.com" />
            </Form.Item>

            <Form.Item
              name="telefono"
              label="Teléfono (WhatsApp)"
              extra={<span style={{ color: '#888' }}>Sin 0, sin 15 y sin espacios. Ej: 3534123456</span>}
              rules={[
                { required: true, message: 'El teléfono es requerido' },
                { pattern: /^[1-9]\d{9}$/, message: 'Ingresá 10 dígitos sin 0 ni 15' },
              ]}
            >
              <Input placeholder="Ej: 3534123456" maxLength={10} />
            </Form.Item>

            <Form.Item
              name="categoria"
              label="Categoría"
              rules={[{ required: true, message: 'La categoría es requerida' }]}
            >
              <Select
                placeholder="Seleccione la categoría"
                loading={loadingCategoriasAcademia}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              >
                {categoriasAcademia.map((cat) => (
                  <Select.Option key={cat.cat_id} value={cat.cat_id} label={categoriaLabel(cat)}>
                    {categoriaLabel(cat)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button onClick={() => setMostrarFormularioAcademia(false)}>Volver</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submittingSolicitudAcademia}
                style={{ background: '#ff9f43', borderColor: '#ff9f43' }}
              >
                Enviar solicitud
              </Button>
            </div>
          </Form>
        )}
      </Modal>

      <Modal
        open={!!modalDifusion}
        onCancel={() => setModalDifusion(null)}
        footer={null}
        width={Math.min(isMobile ? '100%' : 520, typeof window !== 'undefined' ? window.innerWidth - 32 : 520)}
        styles={{
          content: { background: '#1a1a1a', borderRadius: 16 },
          body: { padding: 0 },
        }}
        closeIcon={<CloseOutlined style={{ color: '#fff', fontSize: 22 }} />}
      >
        {modalDifusion && (
          <div
            style={{
              background: '#1a1a1a',
              borderRadius: 16,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: '100%',
                height: isMobile ? 360 : 480,
                minHeight: isMobile ? 360 : 480,
                background: '#0d0d0d',
                position: 'relative',
              }}
            >
              {(modalDifusion.imagenes || []).filter((i) => i.dif_imagen_base64).length > 0 ? (
                <>
                  <Carousel
                    ref={modalCarouselRef}
                    dots
                    dotPosition="bottom"
                    style={{ height: '100%', width: '100%' }}
                  >
                    {(modalDifusion.imagenes || [])
                      .filter((i) => i.dif_imagen_base64)
                      .map((img, idx) => (
                        <div
                          key={idx}
                          style={{
                            height: isMobile ? 360 : 480,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#0d0d0d',
                          }}
                        >
                          <img
                            src={`data:${img.dif_imagen_tipo};base64,${img.dif_imagen_base64}`}
                            alt=""
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        </div>
                      ))}
                  </Carousel>
                  {(modalDifusion.imagenes || []).filter((i) => i.dif_imagen_base64).length > 1 && (
                    <>
                      <Button
                        type="text"
                        icon={<LeftOutlined />}
                        onClick={() => modalCarouselRef.current?.prev()}
                        style={{
                          position: 'absolute',
                          left: 12,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 10,
                          color: '#fff',
                          background: 'rgba(0,0,0,0.5)',
                          width: 44,
                          height: 44,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: 'none',
                        }}
                      />
                      <Button
                        type="text"
                        icon={<RightOutlined />}
                        onClick={() => modalCarouselRef.current?.next()}
                        style={{
                          position: 'absolute',
                          right: 12,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 10,
                          color: '#fff',
                          background: 'rgba(0,0,0,0.5)',
                          width: 44,
                          height: 44,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: 'none',
                        }}
                      />
                    </>
                  )}
                </>
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PictureOutlined style={{ fontSize: 64, color: '#333' }} />
                </div>
              )}
            </div>
            <div style={{ padding: 20, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <Paragraph
                style={{
                  margin: 0,
                  color: 'rgba(255,255,255,0.95)',
                  fontSize: 15,
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {modalDifusion.dif_descripcion || '—'}
              </Paragraph>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Portada;
